import { createFileRoute } from '@tanstack/react-router'
import Banner from '../components/Banner'
import Breadcrumb from '../components/Breadcrumb'
import { getRTWFlights } from '../services/API'
import { trackPhoneCallClick } from '../services/tracking'
import { sanitizeImageUrl, getCurrencySymbol } from '../imageUrl'
import type { RTWFlight } from '../Types'

const BANNER_IMAGE = 'https://cdn.trailfinders.com/images/2705938/artem-beliaikin-v6kii3h5ccu-unsplash.jpg'

export const Route = createFileRoute('/around-the-world')({
  loader: async () => {
    const rtwFlights = await getRTWFlights()
    return { rtwFlights }
  },
  component: AroundTheWorldPage,
})


function RTWFlightItem({ flight }: { flight: RTWFlight }) {
  const currencySymbol = getCurrencySymbol()
  const hasEcoPrice = Number(flight.adv_fare) > 0
  const hasPrePrice = Number(flight.adv_fare_pe) > 0
  const hasBusPrice = Number(flight.adv_fare_Bus) > 0
  const phoneLookup = window.siteVar?.phoneLookup?.flat()
  const ecoPhone = phoneLookup?.[0]?.phoneNumber ?? ''
  const busPhone = phoneLookup?.[1]?.phoneNumber ?? ecoPhone

  const priceText = () => {
    let txt = 'Call us for '
    let tmpArr = []
    if(!hasEcoPrice)tmpArr.push('Economy ')
    if(!hasPrePrice)tmpArr.push('Premium ')
    if(!hasBusPrice)tmpArr.push('Business ')
    txt = txt + tmpArr.join(' & ')
    return txt + ` price${tmpArr.length > 1?'s':''}`
  }

  return (
    <div className="detailed-item border-bottom py-5 bg-white rounded-3 border border-1-dark mb-3">
      <div className="row p-3">

        {/* Left column: map, route details, price */}
        <div className="col-12 col-lg-6">
        <h3 className="section-heading mb-0 text-center d-block d-lg-none">{flight.adv_dest}</h3>
          {flight.rtw_Image && (
            <img
              src={sanitizeImageUrl(flight.rtw_Image, { width: 700 })}
              className="img-fluid mb-4 mt-3 w-100"
              alt={`${flight.adv_dest} map`}
            />
          )}

          <ul className="list-underline">
            {flight.adv_route && (
              <li>
                <span>Route:</span>{' '}
                <span className="text-strong" dangerouslySetInnerHTML={{ __html: flight.adv_route }} />
              </li>
            )}
            <li>
              <span>Included:</span>&nbsp;
              {flight.adv_inclusions
                ? <span dangerouslySetInnerHTML={{ __html: flight.adv_inclusions }} />
                : <span className="text-strong">All flights, fuel surcharges &amp; pre-paid taxes</span>
              }
            </li>
            {flight.hotels && (
              <li>
                <span>Hotels:</span>{' '}
                <span className="text-strong" dangerouslySetInnerHTML={{ __html: flight.hotels }} />
              </li>
            )}
            {flight.rails && (
              <li>
                <span>Rail Journeys Included:</span>{' '}
                <span dangerouslySetInnerHTML={{ __html: flight.rails }} />
              </li>
            )}
          </ul>

          <div className="bg-light rounded-3 border border-1-secondary p-3 mb-3">
            
              <small className="text-muted text-xs">Price from</small>
            <div>
              {hasEcoPrice
                ? <strong>Economy: {currencySymbol}{Number(flight?.adv_fare).toLocaleString()}</strong>
                : <></>
              }
            </div>
            <div>
              {hasPrePrice
                ? <strong>Premium: {currencySymbol}{Number(flight?.adv_fare_pe).toLocaleString()}</strong>
                : <></>
              }
            </div>
            <div>
              {hasBusPrice
                ? <strong>Business: {currencySymbol}{Number(flight?.adv_fare_Bus).toLocaleString()}</strong>
                : <></>
              }
            </div>
           
              <><small className="text-muted">{priceText()}</small></>
            
            <div className='pt-3'>
            <hr/>
            <small className="text-muted fst-italic text-sm">
              Changing the route and stopover from the example above will affect the price of your ticket and the taxes payable. Your Trailfinders Travel Consultant will advise you of the exact fare when you call.
            </small>
            </div>

          </div>

          <div className="bg-light p-3 rounded-3 border border-dark-1 text-end position-relative mb-3">
            <span
              className='position-absolute bg-white bg-opacity-50 rounded-circle start-0 top-0 border border-dark-1 d-inline-flex align-items-center justify-content-center m-2'
              style={{ width: '1.5rem', height: '1.5rem' }}
            >
              <i className="icon-call_light tf-orange"></i>
            </span>
            
            {ecoPhone && (
              <p className="mb-0">
                <small>Economy & Premium :  
                <a
                  href={`tel:${ecoPhone}`}
                  className="fw-bold text-dark"
                  onClick={trackPhoneCallClick}
                  data-ga4-label="tailormade_travel_worldwide"
                  data-ga4-category="text_link"
                > {ecoPhone}</a>
                </small>
              </p>
            )}
      
            {busPhone && (
              <p className="mb-0">
                <small>Business : 
                <a
                  href={`tel:${busPhone}`}
                  className="fw-bold text-dark"
                  onClick={trackPhoneCallClick}
                  data-ga4-label="first_&_business_class_travel"
                  data-ga4-category="text_link"
                > {busPhone}</a>
                </small> 
              </p>
            )}
          </div>

        </div>

        {/* Right column: destination images + copy */}
        <div className="col-12 col-lg-6">
          <h3 className="section-heading mb-0 text-center d-none d-lg-block">{flight.adv_dest}</h3>

          {flight.rtw_DestinationImages && flight.rtw_DestinationImages.length > 0 && (
            <div className="row row-cols-3 g-2 py-3">
              {flight.rtw_DestinationImages.filter(Boolean).map((img, i) => (
                <div key={i} className="col">
                  <img
                    className="img-fluid shadow-sm d-none d-md-block"
                    src={sanitizeImageUrl(img, { width: 180 })}
                    alt={flight.adv_dest}
                    style={{ objectFit: 'cover' }}
                  />
                </div>
              ))}
            </div>
          )}

          {flight.adv_Copy && (
            <div className="mt-3">
              <p dangerouslySetInnerHTML={{ __html: flight.adv_Copy }} />
            </div>
          )}

          {flight.whatelse && (
            <div className="alert alert-secondary mt-3">
              <h4>What else can I do with this ticket?</h4>
              <div dangerouslySetInnerHTML={{ __html: flight.whatelse }} />
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

function AroundTheWorldPage() {
  const { rtwFlights } = Route.useLoaderData()

  return (
    <div className="d-flex flex-column container-fluid p-0">
      <Breadcrumb items={[{ label: 'Round the World Tickets' }]} />

      <Banner title="Round the World Flights &amp; Tickets" image={BANNER_IMAGE} />

      {/* Intro */}
      <section className="container-fluid py-5">
        <div className="container">
          <h3 className="section-heading text-center">ROUND THE WORLD WITH TRAILFINDERS</h3>
          <div className="row justify-content-center">
            <div className="col-12 col-sm-8 text-center">
              <p><strong className="fw-bold fs-5">VAST EXPERTISE:</strong> Trailfinders has been creating unique round the world tickets and multi city flights for over 50 years.
                Because we've been doing it longer than anyone else, we have a wealth of round the world knowledge and experience that you won't find elsewhere.</p>
              <p><strong className="fw-bold fs-5">FLEXIBILITY:</strong> We appreciate plans may change last minute while on your round the world journey, which is why most of the tickets we offer are flexible in terms of changing dates, or even destinations. Based on the trip you're doing, our travel experts are able to suggest a ticket with the right level of flexibility to suit your needs.</p>
              <p><strong className="fw-bold fs-5">OVERLAND OPTIONS:</strong> You don't even need to fly between each stop and there are some fantastic overland (or overwater)
                options: why not road trip down the Pacific Coast Highway between San Francisco and Los Angeles, set sail on a cruise between Sydney and Auckland, or even
                backpack the classic banana pancake trail between Bangkok and Singapore?</p>
              <p><strong className="fw-bold fs-5">SURPRISINGLY AFFORDABLE:</strong> Below are just a few examples of our most popular and surprisingly affordable round the world
                tickets. Whether you want to add some varied stopovers enroute to/from a holiday in Australia or New Zealand, are gearing up for a gap year or are planning
                an unforgettable trip of a lifetime, we can help you plan your perfect round the world trip.</p>
            </div>
          </div>
        </div>
      </section>

      {/* RTW listings */}
      <section className="container-fluid bg-light py-5">
        <div className="container">
          <h3 className="section-heading pt-2 text-center">Popular Round the World Flights</h3>
          <div className="product-detailed">
            {rtwFlights.map((flight, i) => (
              <RTWFlightItem key={i} flight={flight} />
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
